import { useEffect, useRef, useState, useCallback } from 'react';

export type FJWidgetType = 'ECOCAL' | 'Voice' | 'TS' | 'NEWS';

export interface FinancialJuiceWidgetProps {
  widgetType: FJWidgetType;
  mode?: string;
  title: string;
  onRemove: () => void;
}

// Our dark theme colors
const THEME = {
  bgColor: '0A0A0A',      // dark-bg (without #)
  fontColor: '9ca3af',    // gray-400 (without #)
  surfaceColor: '161616', // dark-surface
};

// Configuration for health checks and reloads
const HEALTH_CHECK_CONFIG = {
  initialDelay: 5000,        // Wait 5s after load before first check
  checkInterval: 30000,      // Check every 30s
  reloadCooldown: 60000,     // Wait 60s between reloads (rate limit protection)
  maxConsecutiveReloads: 3,  // Stop after 3 failed reload attempts
};

export function FinancialJuiceWidget({
  widgetType,
  mode = 'standard',
  title,
  onRemove,
}: FinancialJuiceWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);
  const [iframeKey, setIframeKey] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasLoadedSuccessfully, setHasLoadedSuccessfully] = useState(false);
  const [currentBreakpoint, setCurrentBreakpoint] = useState<string>('lg');
  
  // Refs for health monitoring
  const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const healthCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastReloadTimeRef = useRef<number>(0);
  const consecutiveReloadsRef = useRef<number>(0);
  const lastHealthyTimeRef = useRef<number>(Date.now());

  // Check if iframe content is healthy
  const checkIframeHealth = useCallback((): boolean => {
    const iframe = iframeRef.current;
    if (!iframe) return false;

    try {
      // Check if iframe has content
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!iframeDoc) return false;

      // Check if the widget container exists and has content
      const widgetContainer = iframeDoc.getElementById(`fj-widget-${widgetType.toLowerCase()}`);
      if (!widgetContainer) return false;

      // Check the actual content - look for error messages
      const containerText = widgetContainer.textContent?.trim() || '';
      const containerHTML = widgetContainer.innerHTML?.trim() || '';
      
      // If it contains error messages, it's not healthy
      if (containerText.includes('Failed to load') || 
          containerText.includes('failed to load') ||
          containerText.includes('Widget load timeout') ||
          containerText.includes('Widget library unavailable')) {
        return false;
      }
      
      // Widget is healthy if it has actual widget elements (not just error text)
      // FJ widgets create multiple child divs/tables when loaded properly
      const hasWidgetElements = widgetContainer.children.length > 0 && 
                                containerHTML.length > 200; // Real widget has substantial HTML
      
      if (hasWidgetElements) {
        setHasLoadedSuccessfully(true);
        return true;
      }
      
      return false;
    } catch {
      // If we can't access iframe content AND we haven't loaded successfully yet,
      // it means the script probably failed to load
      if (!hasLoadedSuccessfully) {
        return false;
      }
      // If we've loaded successfully before but now can't access,
      // assume it's healthy (cross-origin restriction after navigation)
      return true;
    }
  }, [widgetType, hasLoadedSuccessfully]);

  // Smart reload with rate limiting
  const reloadWidget = useCallback(() => {
    const now = Date.now();
    const timeSinceLastReload = now - lastReloadTimeRef.current;

    // Check if we're in cooldown period
    if (timeSinceLastReload < HEALTH_CHECK_CONFIG.reloadCooldown) {
      console.log(`FinancialJuice Widget ${widgetType} : Reload cooldown active (${Math.round((HEALTH_CHECK_CONFIG.reloadCooldown - timeSinceLastReload) / 1000)}s remaining)`);
      return;
    }

    // Check if we've exceeded max consecutive reloads
    if (consecutiveReloadsRef.current >= HEALTH_CHECK_CONFIG.maxConsecutiveReloads) {
      console.error(`FinancialJuice Widget ${widgetType} : Max reload attempts reached. Widget may be unavailable.`);
      setError('Widget temporarily unavailable. Please try again later.');
      return;
    }

    console.log(`FinancialJuice Widget ${widgetType} Reloading widget`);
    lastReloadTimeRef.current = now;
    consecutiveReloadsRef.current += 1;
    setHasLoadedSuccessfully(false); // Reset load state
    setIsLoading(true);
    setError(null);
    setIframeKey((k) => k + 1);
  }, [widgetType]);

  // Monitor iframe health periodically
  useEffect(() => {
    // Clear any existing interval
    if (healthCheckIntervalRef.current) {
      clearInterval(healthCheckIntervalRef.current);
    }

    // Don't start health checks if there's an error
    if (error) return;

    // Initial check after SHORT delay for faster error detection
    const initialTimeout = setTimeout(() => {
      const isHealthy = checkIframeHealth();
      
      if (isHealthy) {
        console.log(`FinancialJuice Widget ${widgetType} : Initial health check passed`);
        setIsLoading(false);
        lastHealthyTimeRef.current = Date.now();
        consecutiveReloadsRef.current = 0; // Reset counter on success
      } else {
        console.warn(`FinancialJuice Widget ${widgetType} : Initial health check failed`);
        // Check again after 2 more seconds before giving up
        setTimeout(() => {
          const recheckHealthy = checkIframeHealth();
          if (!recheckHealthy) {
            console.error(`FinancialJuice Widget ${widgetType} : Initial load failed after recheck`);
            setError('Widget temporarily unavailable. Please try again later.');
            setIsLoading(false);
          }
        }, 2000);
      }

      // Start periodic health checks
      healthCheckIntervalRef.current = setInterval(() => {
        const isHealthy = checkIframeHealth();
        
        if (isHealthy) {
          console.log(`FinancialJuice Widget : ${widgetType} : Health check passed`);
          lastHealthyTimeRef.current = Date.now();
          consecutiveReloadsRef.current = 0; // Reset counter on success
          setIsLoading(false);
        } else {
          const timeSinceHealthy = Date.now() - lastHealthyTimeRef.current;
          console.warn(`FinancialJuice Widget ${widgetType} : Health check failed. Unhealthy for ${Math.round(timeSinceHealthy / 1000)}s`);
          
          // Only reload if unhealthy for more than 10 seconds
          if (timeSinceHealthy > 10000) {
            reloadWidget();
          }
        }
      }, HEALTH_CHECK_CONFIG.checkInterval);
    }, HEALTH_CHECK_CONFIG.initialDelay);

    return () => {
      clearTimeout(initialTimeout);
      if (healthCheckIntervalRef.current) {
        clearInterval(healthCheckIntervalRef.current);
      }
    };
  }, [checkIframeHealth, reloadWidget, error, widgetType]);

  // Debounced resize observer
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateDimensions = () => {
      const width = container.clientWidth;
      const height = container.clientHeight;
      if (width > 0 && height > 0) {
        setDimensions((prev) => {
          // Only update if dimensions actually changed significantly (>10px difference)
          if (prev && Math.abs(prev.width - width) < 10 && Math.abs(prev.height - height) < 10) {
            return prev;
          }
          return { width, height };
        });
      }
    };

    // Initial dimensions
    updateDimensions();

    // Debounced resize observer
    const observer = new ResizeObserver(() => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = setTimeout(updateDimensions, 500);
    });

    observer.observe(container);

    return () => {
      observer.disconnect();
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);

  // Track last iframe dimensions
  const lastIframeDimensionsRef = useRef<{ width: number; height: number } | null>(null);

  // Determine breakpoint from window width
  const getBreakpoint = useCallback((): string => {
    const width = window.innerWidth;
    if (width >= 1200) return 'lg';
    if (width >= 768) return 'md';
    if (width >= 480) return 'sm';
    return 'xs';
  }, []);

  // Reload on breakpoint changes OR significant dimension changes during initial layout period
  useEffect(() => {
    if (!dimensions) return;
    
    const lastDims = lastIframeDimensionsRef.current;
    const newBreakpoint = getBreakpoint();
    
    // Initial load
    if (!lastDims) {
      lastIframeDimensionsRef.current = dimensions;
      setCurrentBreakpoint(newBreakpoint);
      setIframeKey((k) => k + 1);
      setIsLoading(true);
      return;
    }
    
    // Reload if BREAKPOINT changed
    if (newBreakpoint !== currentBreakpoint) {
      console.log(`FinancialJuice Widget ${widgetType}: Breakpoint changed from ${currentBreakpoint} to ${newBreakpoint}`);
      lastIframeDimensionsRef.current = dimensions;
      setCurrentBreakpoint(newBreakpoint);
      setIframeKey((k) => k + 1);
      setIsLoading(true);
      return;
    }

    // Reload if dimensions changed significantly (handles user resizing widget)
    const widthChange = Math.abs(lastDims.width - dimensions.width);
    const heightChange = Math.abs(lastDims.height - dimensions.height);

    if (widthChange > 50 || heightChange > 50) {
      console.log(`FinancialJuice Widget ${widgetType}: Dimension change (w: ${widthChange}px, h: ${heightChange}px)`);
      lastIframeDimensionsRef.current = dimensions;
      setIframeKey((k) => k + 1);
      setIsLoading(true);
    }
  }, [dimensions, currentBreakpoint, widgetType, getBreakpoint]);

  // Reload only when widget type or mode changes
  useEffect(() => {
    if (lastIframeDimensionsRef.current) {
      setIframeKey((k) => k + 1);
      setIsLoading(true);
    }
  }, [widgetType, mode]);

  // Calculate width to pass to FinancialJuice - use actual container width
  const getWidgetPassWidth = useCallback((): number => {
    if (!lastIframeDimensionsRef.current) return 300;
    return lastIframeDimensionsRef.current.width;
  }, []);

  // Build iframe with appropriate width
  const iframeSrc = lastIframeDimensionsRef.current
    ? buildIframeContent(widgetType, mode, getWidgetPassWidth())
    : null;

  // Manual reload handler (for user-triggered reload)
  const handleManualReload = () => {
    consecutiveReloadsRef.current = 0; // Reset counter for manual reloads
    lastReloadTimeRef.current = 0; // Allow immediate reload
    setHasLoadedSuccessfully(false); // Reset load state
    setError(null);
    reloadWidget();
  };

  return (
    <div className="h-full flex flex-col bg-dark-surface rounded-lg overflow-hidden">
      {/* Title Bar - drag handle only */}
      <div className="widget-drag-handle flex items-center px-3 py-1.5 bg-chrome border-b border-dark-border cursor-move relative z-20">
        <h3 className="text-sm font-bold text-white uppercase tracking-[1.5px]">{title}</h3>
        {isLoading && (
          <span className="ml-2 text-xs text-gray-500">Loading...</span>
        )}
      </div>

      {/* Toolbar - NOT draggable */}
      <div className="flex items-center justify-end gap-2 px-3 py-1 bg-dark-bg/50 border-b border-dark-border relative z-20">
        {error && (
          <button
            onClick={handleManualReload}
            className="text-xs text-gray-400 hover:text-white hover:bg-blue-500/20 px-2 py-0.5 rounded transition-colors"
            title="Retry loading widget"
          >
            ↻ Retry
          </button>
        )}
        <button
          onClick={onRemove}
          className="text-gray-400 hover:text-white hover:bg-bear-red/20 text-sm px-2 py-0.5 rounded transition-colors"
          title="Close widget"
        >
          ×
        </button>
      </div>

      {/* Widget Container */}
      <div ref={containerRef} className="flex-1 overflow-hidden">
        {error ? (
          <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 p-4 text-center">
            <p className="mb-2">{error}</p>
            <button
              onClick={handleManualReload}
              className="text-sm text-blue-400 hover:text-blue-300 underline"
            >
              Try again
            </button>
          </div>
        ) : iframeSrc ? (
          <iframe
            ref={iframeRef}
            key={iframeKey}
            title={title}
            srcDoc={iframeSrc}
            className="w-full h-full border-0"
            sandbox="allow-scripts allow-same-origin allow-popups"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            Loading widget...
          </div>
        )}
      </div>
    </div>
  );
}

function buildIframeContent(
  widgetType: FJWidgetType,
  mode: string,
  width: number
): string {
  const containerId = `fj-widget-${widgetType.toLowerCase()}`;

  // Use feed.financialjuice.com for all widgets to avoid CORS issues
  const scriptUrl = 'https://feed.financialjuice.com/widgets/widgets.js';

  // Build options based on widget type
  let optionsJs = `
    options.container = "${containerId}";
    options.widgetType = "${widgetType}";
  `;

  // Add type-specific options
  switch (widgetType) {
    case 'ECOCAL':
      optionsJs += `
        options.mode = "standard";
        options.width = "${width}px";
        options.height = "100%";
        options.backColor = "${THEME.bgColor}";
        options.fontColor = "${THEME.fontColor}";
      `;
      break;
    case 'NEWS':
      optionsJs += `
        options.mode = "dark";
        options.width = "100%";
        options.height = "100%";
        options.backColor = "${THEME.bgColor}";
        options.fontColor = "${THEME.fontColor}";
      `;
      break;
    case 'Voice':
      optionsJs += `
        options.mode = "${mode}";
      `;
      break;
    case 'TS':
      optionsJs += `
        options.mode = "inline";
      `;
      break;
  }

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        html, body {
          width: 100%;
          height: 100%;
          
          color: #${THEME.fontColor};
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          overflow: auto;
        }
        #${containerId} {
          width: 100%;
          height: 100%;
        }
        /* Override widget styles for dark theme */
        .fj-widget, .fj-eco-widget, .fj-news-widget, .fj-voice-widget, .fj-ts-widget {
          background-color: #${THEME.bgColor} !important;
          color: #${THEME.fontColor} !important;
        }
        /* Style scrollbars */
        ::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        ::-webkit-scrollbar-track {
          background: #${THEME.surfaceColor};
        }
        ::-webkit-scrollbar-thumb {
          background: #2B2B2B;
          border-radius: 3px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: #3d4558;
        }
      </style>
    </head>
    <body>
      <div id="${containerId}"></div>
      <script type="text/javascript">
        (function() {
          var jo = document.createElement("script");
          jo.type = "text/javascript";
          jo.id = "FJ-Widgets";
          jo.src = "${scriptUrl}";
          
          // Add timeout for script loading
          var scriptTimeout = setTimeout(function() {
            if (!window.FJWidgets) {
              console.error('FinancialJuice Widget Script load timeout');
              document.getElementById("${containerId}").innerHTML =
                '<div style="padding: 20px; text-align: center; color: #9ca3af;">Widget load timeout</div>';
            }
          }, 10000); // 10 second timeout
          
          jo.onload = function() {
            clearTimeout(scriptTimeout);
            if (window.FJWidgets && window.FJWidgets.createWidget) {
              try {
                var options = {};
                ${optionsJs}
                new window.FJWidgets.createWidget(options);
              } catch (error) {
                console.error('FinancialJuice Widget : Widget creation error:', error);
                document.getElementById("${containerId}").innerHTML =
                  '<div style="padding: 20px; text-align: center; color: #9ca3af;">Failed to initialize widget</div>';
              }
            } else {
              console.error('FinancialJuice Widget : Widget library not available');
              document.getElementById("${containerId}").innerHTML =
                '<div style="padding: 20px; text-align: center; color: #9ca3af;">Widget library unavailable</div>';
            }
          };
          
          jo.onerror = function() {
            clearTimeout(scriptTimeout);
            console.error('FinancialJuice Widget Script load error');
            document.getElementById("${containerId}").innerHTML =
              '<div style="padding: 20px; text-align: center; color: #9ca3af;">Failed to load widget</div>';
          };
          
          document.getElementsByTagName("head")[0].appendChild(jo);
        })();
      </script>
    </body>
    </html>
  `;
}